import { useMemo } from "react";

/**
 * useStepSaveGuard
 *
 * Centralized step-aware save guard.
 *
 * @param {Object} params
 * @param {number|Object} params.activeStep
 * @param {Object<number, function():boolean>} params.stepValidators
 *        Map of stepIndex → validator function returning TRUE if step is valid
 * @param {boolean} params.defaultHasError
 *        Fallback error state for steps without explicit validator
 *
 * @returns {{
 *   activeStepIndex: number,
 *   canSave: boolean,
 *   hasSaveError: boolean
 * }}
 */
export const useStepSaveGuard = ({ activeStep, stepValidators = {}, defaultHasError = false }) => {
	// Normalize step index defensively (number, numeric string, or payload object)
	const normalizeStepIndex = (value) => {
		if (typeof value === "number" && Number.isFinite(value)) return value;
		if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
			return Number(value);
		}
		if (value && typeof value === "object") {
			const candidate = value.index ?? value.step;
			if (typeof candidate === "number" && Number.isFinite(candidate)) return candidate;
			if (
				typeof candidate === "string" &&
				candidate.trim() !== "" &&
				!Number.isNaN(Number(candidate))
			) {
				return Number(candidate);
			}
		}
		return 0;
	};

	const activeStepIndex = normalizeStepIndex(activeStep);

	const canSave = useMemo(() => {
		const validator = stepValidators[activeStepIndex];

		if (typeof validator === "function") {
			return validator();
		}

		// Fallback behavior
		return !defaultHasError;
	}, [activeStepIndex, stepValidators, defaultHasError]);

	return {
		activeStepIndex,
		canSave,
		hasSaveError: !canSave,
	};
};
